import { useEffect, useState } from 'react';
import { Mail, Loader2, Check } from 'lucide-react';
import api from '../lib/api';
import { Switch } from './ui/switch';

/**
 * EmailPreferencesPanel — drop-in section for any role's dashboard "Profile"
 * page. Lets the user toggle which categories of transactional emails they
 * receive. Security/account emails (verify, password reset, approval) are
 * always-on and rendered as locked rows so users see WHY they can't disable
 * them.
 *
 * Backend contract:
 *   GET  /api/me/email-preferences  -> { preferences: {category: bool, ...},
 *                                        categories: {category: {label, description, optional}} }
 *   PUT  /api/me/email-preferences  -> { preferences: ... }
 *
 * Optimistic toggle UX: flip the local state first, send PUT in the
 * background, snap back + show a small error message if the PUT fails.
 */
export default function EmailPreferencesPanel() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [savedMsg, setSavedMsg] = useState('');
    const [categories, setCategories] = useState({});
    const [prefs, setPrefs] = useState({});
    const [savingKey, setSavingKey] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/me/email-preferences');
                if (cancelled) return;
                setCategories(res.data.categories || {});
                setPrefs(res.data.preferences || {});
            } catch {
                if (!cancelled) setError('Failed to load email preferences.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const togglePref = async (key) => {
        const previous = prefs[key];
        const nextPrefs = { ...prefs, [key]: !previous };
        setPrefs(nextPrefs);
        setSavingKey(key);
        setError('');
        try {
            // Send only the optional keys — the backend silently drops critical ones anyway.
            const optionalPayload = {};
            for (const [k, meta] of Object.entries(categories)) {
                if (meta.optional) optionalPayload[k] = nextPrefs[k];
            }
            await api.put('/me/email-preferences', { preferences: optionalPayload });
            setSavedMsg('Preferences saved');
            setTimeout(() => setSavedMsg(''), 2500);
        } catch {
            setPrefs(p => ({ ...p, [key]: previous })); // revert
            setError('Could not save — please try again.');
        } finally {
            setSavingKey(null);
        }
    };

    if (loading) {
        return (
            <div className="border border-zinc-200 p-6 flex items-center gap-2 text-sm text-zinc-500" data-testid="email-preferences-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading email preferences…
            </div>
        );
    }

    const entries = Object.entries(categories);
    if (entries.length === 0) {
        return null;
    }

    return (
        <div className="border border-zinc-200 p-5 sm:p-6" data-testid="email-preferences-panel">
            <div className="flex items-start gap-3 mb-5">
                <Mail className="w-5 h-5 text-zinc-700 mt-0.5" />
                <div className="flex-1">
                    <h3 className="font-heading font-bold text-base text-zinc-900">Email preferences</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">Choose which transactional emails you want to receive. Security and account emails are always sent.</p>
                </div>
            </div>

            {error && (
                <div className="mb-3 bg-red-50 border border-red-200 p-2 text-xs text-red-700" data-testid="email-prefs-error">{error}</div>
            )}
            {savedMsg && (
                <div className="mb-3 bg-green-50 border border-green-200 p-2 text-xs text-green-700 flex items-center gap-2" data-testid="email-prefs-saved">
                    <Check className="w-3.5 h-3.5" /> {savedMsg}
                </div>
            )}

            <div className="divide-y divide-zinc-100">
                {entries.map(([key, meta]) => {
                    const checked = prefs[key] !== false; // default to true
                    const locked = !meta.optional;
                    const saving = savingKey === key;
                    return (
                        <div key={key} className="flex items-center justify-between py-3 gap-4" data-testid={`email-pref-row-${key}`}>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                                    {meta.label}
                                    {locked && (
                                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Always on</span>
                                    )}
                                </p>
                                <p className="text-xs text-zinc-500 mt-0.5">{meta.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />}
                                <Switch
                                    checked={locked ? true : checked}
                                    disabled={locked || saving}
                                    onCheckedChange={() => !locked && togglePref(key)}
                                    data-testid={`email-pref-toggle-${key}`}
                                    aria-label={`Toggle ${meta.label} emails`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
